import { Calendar, CalendarChangeEvent } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { classNames, classNames as cx } from 'primereact/utils';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

import { LabelValue } from '../../LabelValue/LabelValue';
import { fieldsMaxLength } from '../../../config/fields';
import styles from '../styles.module.scss';
import { IFormProps, TDataItem } from '../types';

import Checkboxes from './Checkboxes';
import ButtonPrimary from "../../../uiComponents/ButtonPrimary/ButtonPrimary";
import { ConfirmDialog } from "primereact/confirmdialog";
import AddTinDialog from './AddTinDialog';

import moment from 'moment';
import { useToast } from '../../../hooks/useToast';
import isValidUSState from "../../../helpers/us_states";



const roundingModes = [
  { label: 'Round up', code: 'ROUND_UP' },
  { label: 'Round down', code: 'ROUND_DOWN' },
];

const Form: FC<IFormProps> = ({
  fieldsConfig,
  onSubmit,
  addressGroupFields,
  negativeValueWarning,
  isSupposeExists,
  setSuppose,
  captureFirstRecipientEmailAfterEmailFieldOnBlur,
  form,
  active,
  total,
  setFormStep,
  isPublic,
  showSelectTemplate,
  disableFiling,
  isPublicDraft,
}) => {
  
  const { show } = useToast();
  const [addTinDialogOpen, setAddTinDialogOpen] = useState(false);
  const [isSpouseTinSelected, setSpouseTinSelected] = useState(false);
  const [taxableYearWarning, setTaxableYearWarning] = useState(false);
  const [countryStateWarning, setCountryStateWarning] = useState(false);

  const hiddenFormButtonRef: React.RefObject<HTMLButtonElement> = useRef(null);
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    register,
    getValues
  } = form;
  
  const allowManualRoundUp = watch('allowManualRoundUp');

  const state = watch("state");
  const countryValue = watch('country');
  const isUSSelected = countryValue === 'United States';
  const isSecondStep = active === 2;
  const backToFormFirstStep = () => {
    setFormStep((prev) => {
      return (
        { ...prev, active: 1 }
      )
    });
  }

  const checkCountryStateMatching = ():boolean => {
    if(!isUSSelected && isValidUSState(state as string)) {
      setCountryStateWarning(true);
      return false;
    }
    return  true;
  }
  const onSubmitHandler = (passCheck: boolean) => {

    if (!passCheck) {
      if (active === 2) {

        const dateOfTransfer = watch('date_of_transfer');
        const date = new Date(dateOfTransfer as string);
        const dateOfTransferYear = date.getFullYear().toString();
        const taxableYear = watch('taxable_year');

        if (dateOfTransfer) {
          // const maxPastDays = Number(getValues('maxDaysPastTransfer')) || 21;
          const maxPastDays = 14;

          const currentDate = moment().utc();
          const cutoffDate = currentDate.clone().subtract(maxPastDays, 'days');
          const formatedDate = moment(dateOfTransfer).format("MMMM D, YYYY");
          const transferDate = moment.utc(formatedDate, "MMMM D, YYYY");

          if (!transferDate.isBetween(cutoffDate, currentDate, "days", "[]")) {
            show({
              severity: 'error',
              detail: "The grant date for this record exceeds the cutoff period. Unfortunately, it is no longer eligible for processing through Corpora."
            });
            return;
          }
        }

        if (dateOfTransferYear !== taxableYear) {
          setTaxableYearWarning(true);
          return;
        }
    if (!checkCountryStateMatching()){
      return;
    }
      } else {
        const ssn = watch('taxpayer_id') as string;
        const onlyDigits = /^\d{9}$/;
        const formatSSN = /^\d{3}-\d{2}-\d{4}$/;
        const onlyLetters = /^[A-Za-z\s\/\\,\.]+$/;

        if (!(onlyDigits.test(ssn) || formatSSN.test(ssn)) && !onlyLetters.test(ssn)) {
          onSubmitHandler(true);
        }
      }
    }



    if (hiddenFormButtonRef.current) {
      hiddenFormButtonRef.current.click();
    }
  };

  const { minDate, maxDate } = useMemo(() => {

    // const range = Number(getValues('maxDaysPastTransfer')) || 21;
    const range = 14;
    const minDate = moment().utc().subtract(range, 'days').toDate();
    const maxDate = moment().utc().add(1, 'day').toDate();

    return { minDate, maxDate };

  }, [getValues]);
    

  const getFormErrorMessage = (name: string) => {
    return (
      errors[name] && (
        <small className={cx('p-error', styles.errorMessage)}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {errors[name].message}
        </small>
      )
    );
  };

  const toolTipErrorHolder = useCallback(
    (fieldName: string) => {
      return (
        <>
          <span data-name={fieldName}>{getFormErrorMessage(fieldName)}</span>
        </>
      );
    },
    [getFormErrorMessage],
  );

  const currentStepFieldsConfig = useMemo(() => {
    return fieldsConfig
      .filter((item) => item.step === active)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [fieldsConfig, active]);

  return (
    <>
      <form
        id={`${isPublic ? "public-regualr-83b": "regualr-83b"}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmitHandler(false);
          }
        }}
        className={cx(styles.formWrapper)}
        onSubmit={handleSubmit(onSubmit)}
        autoComplete={`off-${Math.random().toString(36).substring(7)}`}
      >
        <div className={styles.scrollWrapper}>
          {
            showSelectTemplate && !isSecondStep && (
              <Checkboxes
                isSupposeExists={isSupposeExists}
                setSuppose={setSuppose}
              />
            )
          }
          <div className={styles.formFields}>
            {fieldsConfig &&
              currentStepFieldsConfig.map((item: TDataItem) => {
                const {
                  comment,
                  configName: fieldName,
                  configType,
                  label,
                  options,
                  configGroup,
                  required,
                  readOnly,
                }: TDataItem = item;
                const isCurrencyField =
                  fieldName === 'price_per_share' || fieldName === 'price_paid' || fieldName.startsWith("r_");
                const isNumberOfSharesField = fieldName === 'number_of_shares';
                const isFirstRecipientEmail = fieldName === 'firstRecipientEmail';
                const numberOfSharesMaxValue = 100000000;
                const isCountryField = fieldName === 'country';
                const readOnlyProperties = {
                  readOnly: true,
                  disabled: true,
                };

                const readOnlyFieldProps = (readOnly || (isFirstRecipientEmail && isPublicDraft)) ? readOnlyProperties : {};
                if (
                  configGroup &&
                  Object.prototype.hasOwnProperty.call(
                    addressGroupFields,
                    configGroup,
                  )
                ) {

                  return (
                    <div
                      className={styles.multiGridWrapper}
                      key={`${fieldName}${configType}`}
                    >
                      {(addressGroupFields[configGroup] || []).map(
                        (item, index) => {
                          const {
                            comment,
                            configName: groupedFieldName,
                            label: groupedLabel,
                            required: groupedRequired,
                            configType: groupedConfigType,
                            options: groupedOptions,
                          } = item;
                          return (
                            <div
                              key={groupedLabel}
                              className={
                                [
                                  styles.cityCol,
                                  styles.stateCol,
                                  styles.zipCol,
                                ][index]
                              }
                            >
                              {groupedConfigType === 'ENUM' && isUSSelected ? (
                                <LabelValue
                                  label={groupedLabel}
                                  required={true}
                                  fieldName={groupedFieldName}
                                  comment={comment}
                                >
                                  <Controller
                                    name={groupedFieldName}
                                    control={control}
                                    render={({ field }) => {
                                      return (
                                        <Dropdown
                                          id={field.name}
                                          value={field.value}
                                          onChange={(e) => {
                                            const newValue = e.value;
                                            field.onChange(newValue);
                                          }}
                                          options={groupedOptions || []}
                                          style={{ width: '100%' }}
                                          {...readOnlyFieldProps}
                                        />
                                      );
                                    }}
                                  />
                                  {toolTipErrorHolder(
                                    groupedFieldName,
                                  )}
                                </LabelValue>
                              ) : (
                                <LabelValue
                                  label={groupedLabel}
                                  required={groupedRequired}
                                  fieldName={groupedFieldName}
                                  comment={comment}
                                >
                                  <Controller
                                    name={groupedFieldName}
                                    control={control}
                                    render={({ field }) => {
                                      return (
                                        <InputText
                                          {...field}
                                          value={field?.value as string}
                                          readOnly={readOnly}
                                          maxLength={fieldsMaxLength.name}
                                          style={{ width: '100%' }}
                                          type="text"
                                          autoComplete={`off-${Math.random()
                                            .toString(36)
                                            .substring(7)}`}
                                          onChange={(e) => {
                                              const newValue = e.target.value;
                                              if (/^\s+$/.test(newValue)) {
                                                  field.onChange("");
                                              } else {
                                                  field.onChange(newValue);
                                              }
                                          }}
                                          {...readOnlyFieldProps}
                                        />
                                      );
                                    }}
                                  />
                                  {toolTipErrorHolder(
                                    groupedFieldName,
                                  )}
                                </LabelValue>
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                  );
                }

                switch (configType) {
                  case 'STRING':
                  case 'SSN':
                  case 'EMAIL':
                    return (
                      <div key={fieldName}>
                        <LabelValue
                          label={label}
                          required={required}
                          fieldName={fieldName}
                          comment={comment}
                        >
                          <Controller
                            name={fieldName}
                            control={control}
                            render={({ field }) => {
                              return (
                                <InputText
                                  {...field}
                                  value={field.value as string}
                                  maxLength={fieldsMaxLength.name}
                                  style={{ width: '100%' }}
                                  type="text"
                                  {
                                    ...((fieldName === "taxpayer_id" || fieldName === "spouse_id") ? {
                                      readOnly:true,
                                      onFocus:(e) => {
                                        setAddTinDialogOpen(true);
                                        setSpouseTinSelected(e.target.name === "spouse_id")
                                      }
                                    } : {
                                     readOnly:readOnly
                                    })
                                  }
                                  autoComplete={`off-${Math.random()
                                    .toString(36)
                                    .substring(7)}`}
                                  onBlur={
                                    (fieldName === 'firstRecipientEmail' && isPublic)
                                      ? captureFirstRecipientEmailAfterEmailFieldOnBlur
                                      : () => null
                                  }
                                  onChange={(e) => {
                                      const newValue = e.target.value;
                                      if (/^\s+$/.test(newValue)) {
                                          field.onChange("");
                                      } else {
                                          field.onChange(newValue);
                                      }
                                  }}
                                  {...readOnlyFieldProps}
                                />
                              );
                            }}
                          />
                          {toolTipErrorHolder(fieldName)}
                        </LabelValue>
                      </div>
                    );
                  case 'NUMERIC':
                  case 'DECIMAL':
                    return (
                      <div key={fieldName}>
                        <LabelValue
                          label={label}
                          required={required}
                          fieldName={fieldName}
                          comment={comment}
                        >
                          <Controller
                            name={fieldName}
                            control={control}
                            render={({ field }) => {
                              return (
                                <InputNumber
                                  {...field}
                                  value={field.value as number}
                                  {...readOnlyFieldProps}
                                  {...(isNumberOfSharesField
                                    ? {
                                      mode: 'decimal',
                                      max: numberOfSharesMaxValue,
                                    }
                                    : {})}
                                  {...(isCurrencyField
                                    ? {
                                      currency: 'USD',
                                      mode: 'currency',
                                      placeholder: '$',
                                      minFractionDigits: 2,
                                      maxFractionDigits: 10,
                                    }
                                    : {})}
                                  style={{ width: '100%' }}
                                  onChange={(e) => {
                                    const isSmaller = e?.value as number <= numberOfSharesMaxValue;
                                    if (isNumberOfSharesField && isSmaller) {
                                      field.onChange(e.value);
                                    }
                                    if (!isNumberOfSharesField) {
                                      field.onChange(e.value);
                                    }
                                  }}
                                />
                              );
                            }}
                          />
                          {toolTipErrorHolder(fieldName)}
                        </LabelValue>
                      </div>
                    );
                  case 'ENUM':
                    return (
                      <div key={fieldName}>
                        <LabelValue
                          label={label}
                          required={required}
                          fieldName={fieldName}
                          comment={comment}
                        >
                          <Controller
                            name={fieldName}
                            control={control}
                            render={({ field }) => {
                              return (
                                <Dropdown
                                  {...readOnlyFieldProps}
                                  id={field.name}
                                  value={field.value}
                                  onChange={(e) => {
                                    const newValue = e.value;
                                    const isNewValUS =
                                      newValue === 'United States';
                                    const stateEmptyVal = '';
                                    if (isCountryField) {
                                      setValue('state', stateEmptyVal);
                                      register('state', {
                                        required: isNewValUS,
                                      });
                                    }
                                    field.onChange(newValue);
                                  }}
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  options={options}
                                  style={{ width: '100%' }}
                                />
                              );
                            }}
                          />
                          {toolTipErrorHolder(fieldName)}
                        </LabelValue>
                      </div>
                    );
                  case 'DATE':
                    return (
                      <div key={fieldName}>
                        <LabelValue
                          label={label}
                          required={required}
                          fieldName={fieldName}
                          comment={comment}
                        >
                          <Controller
                            name={fieldName}
                            control={control}
                            render={({ field }) => {
                              let utcDate = undefined;
                              return (
                                <Calendar
                                  {...readOnlyFieldProps}
                                  id={field.name}
                                  value={field.value ? moment(field.value).toDate() : undefined}
                                  showButtonBar
                                  dateFormat="mm/dd/yy"
                                  style={{ width: '100%' }}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                  placeholder="mm/dd/yy"
                                  onChange={(e: CalendarChangeEvent) => {
                                     field.onChange(e.value);
                                    // const localDate = (e.value instanceof Date) ? e.value : null;
                                    // if (localDate) {
                                    //   const utcMoment = moment(localDate).utc();
                                    //   const utcDate: Date = utcMoment.toDate();
                                    //   field.onChange(utcDate);
                                    // } else {
                                    //   field.onChange(null);
                                    // }
                                  }}

                                />
                              )
                            }}
                          />
                          <i
                            className={classNames(
                              'pi pi-calendar',
                              styles.calendarIcon,
                            )}
                          />
                          {toolTipErrorHolder(fieldName)}
                        </LabelValue>
                      </div>
                    );
                  case 'TEXTAREA':
                    return (
                      <div key={fieldName}>
                        <LabelValue
                          label={label}
                          required={required}
                          fieldName={fieldName}
                          comment={comment}
                        >
                          <Controller
                            name={fieldName}
                            control={control}
                            render={({ field }) => {
                              return (
                                <InputTextarea
                                  {...field}
                                  {...readOnlyFieldProps}
                                  value={field.value as string}
                                  className={styles.textArea}
                                  maxLength={250}
                                  style={{ width: '100%' }}
                                />
                              );
                            }}
                          />
                          {toolTipErrorHolder(fieldName)}
                        </LabelValue>
                      </div>
                    );
                }
              })}

            {
              (allowManualRoundUp && isSecondStep) ? (
                <LabelValue
                  required={false}
                  fieldName="roundingMode"
                  label="Select a rounding option"
                  comment="You may round up or round down the “Total FMV” and “Total Price Paid” values by selecting the respective option.  As a courtesy comment: if you are rounding down, make sure you consult with your legal or tax advisor."
                >
                  <Controller
                    name="roundingMode"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <Dropdown
                            {...field}
                            showClear
                            options={roundingModes}
                            optionLabel="label"
                            optionValue="code"
                            className="w-full"
                            placeholder="Select a rounding option"
                          />
                        </>
                      )
                    }}
                  />
                </LabelValue>

              ) : null
            }
          </div>
          <button
            type="submit"
            ref={hiddenFormButtonRef}
            style={{ opacity: 0, pointerEvents: 'none' }}
          ></button>
        </div>
      </form>
      {negativeValueWarning && (
        <p className={styles.negativeValueWarning}>
          "Price Paid per Share" cannot be larger than "Price per Share".
        </p>
      )}
      <div className={styles.formFooter}>
        <div className={styles.formSteps}>
          <div className={classNames(styles.stepWidget, {
            [styles.fill]: isSecondStep,
          })}>
            <span className={styles.half}>
              <span />
            </span>
            <span className={classNames(styles.half, styles.secondHalf)}>
              <span />
            </span>
            <p>{`${active}/${total}`}</p>
          </div>
          <span className={styles.stepInfo}>
            {
              isSecondStep ? 'Equity info' : 'Personal info'
            }
          </span>
        </div>
        <div className={classNames(styles.formButtons)}>
          {
            isSecondStep && (
              <ButtonPrimary
                label="Back"
                styleType="secondaryOutline"
                type="button"
                onClick={backToFormFirstStep}
              />
            )
          }
          <ButtonPrimary
            type="button"
            onClick={() => onSubmitHandler(false)}
            disabled={disableFiling}
            label="Next"
          />
        </div>
      </div>
      <AddTinDialog 
                visible={addTinDialogOpen}
                setFieldValue={setValue}
                onClose={() => setAddTinDialogOpen(false)}
                isSpouseTinSelected={isSpouseTinSelected} />
      <ConfirmDialog
        visible={taxableYearWarning}
        onHide={
          () => setTaxableYearWarning(false)
        }
        message="The taxable year you provided is different from the year of your grant.  Are you sure this is correct?"
        header="Warning"
        icon="pi pi-check"
        rejectLabel="Go back and fix"
        acceptLabel="Proceed"
        accept={() => onSubmitHandler(checkCountryStateMatching())}
      />

      <ConfirmDialog
          visible={countryStateWarning}
          onHide={
            () => setCountryStateWarning(false)
          }
          message="Did you intend to insert a U.S. address?  If so, you’ve provided a different country in the “Country” field."
          header="Warning"
          icon="pi pi-check"
          rejectLabel="Go back and fix"
          acceptLabel="Proceed"
          accept={() => onSubmitHandler(true)}
      />
    </>
  );
};

export default Form;
